import { useEffect, useMemo, useRef, useState } from "react";
import { Star, Users, Cog, Fuel, MapPin, Check, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Left-column visual + info block for the vehicle detail page.
 *
 * If the vehicle carries a `gallery: []` array (uploaded via admin fleet
 * editor), we render a swipeable carousel with keyboard, mouse, and touch
 * gestures. Otherwise the classic single-image hero is preserved so the
 * component stays backwards-compatible with vehicles that predate the
 * gallery feature.
 */
export default function VehicleGallery({ v }) {
  const images = useMemo(() => {
    const list = [];
    if (v.image_url) list.push(v.image_url);
    for (const g of v.gallery || []) {
      if (g && !list.includes(g)) list.push(g);
    }
    return list;
  }, [v.image_url, v.gallery]);

  const [index, setIndex] = useState(0);
  const trackRef = useRef(null);
  const touchStartX = useRef(null);

  useEffect(() => {
    // If the vehicle prop changes, reset to the primary image.
    setIndex(0);
  }, [v.id]);

  const total = images.length;
  const goTo = (i) => setIndex(((i % total) + total) % total);
  const next = () => goTo(index + 1);
  const prev = () => goTo(index - 1);

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = (e.changedTouches[0].clientX - touchStartX.current);
    if (Math.abs(dx) > 40) (dx < 0 ? next() : prev());
    touchStartX.current = null;
  };

  return (
    <div className="lg:col-span-7" data-testid="vehicle-gallery">
      <div
        className="relative bg-[#EFECE6] overflow-hidden select-none"
        style={{ aspectRatio: "16/10" }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        data-testid="vehicle-gallery-carousel"
      >
        <div
          ref={trackRef}
          className="flex h-full transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${index * 100}%)`, width: `${total * 100}%` }}
        >
          {images.map((src, i) => (
            <img
              key={`${src}-${i}`}
              src={src}
              alt={`${v.name} — photo ${i + 1}`}
              className="h-full object-cover"
              style={{ width: `${100 / total}%` }}
              draggable={false}
              data-testid={`gallery-image-${i}`}
            />
          ))}
        </div>

        {total > 1 && (
          <>
            <button
              type="button"
              onClick={prev}
              className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/85 hover:bg-white text-black rounded-full p-2 shadow transition-colors"
              aria-label="Previous image"
              data-testid="gallery-prev"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              onClick={next}
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/85 hover:bg-white text-black rounded-full p-2 shadow transition-colors"
              aria-label="Next image"
              data-testid="gallery-next"
            >
              <ChevronRight size={18} />
            </button>
            <div
              className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5"
              data-testid="gallery-dots"
            >
              {images.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => goTo(i)}
                  className={`h-1.5 rounded-full transition-all ${i === index ? "w-6 bg-black" : "w-1.5 bg-black/40 hover:bg-black/60"}`}
                  aria-label={`Go to image ${i + 1}`}
                  data-testid={`gallery-dot-${i}`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {total > 1 && (
        <div className="mt-3 flex gap-2 overflow-x-auto pb-1" data-testid="gallery-thumbs">
          {images.map((src, i) => (
            <button
              key={i}
              type="button"
              onClick={() => goTo(i)}
              className={`flex-none w-20 h-14 rounded overflow-hidden border-2 transition-colors ${i === index ? "border-black" : "border-transparent opacity-70 hover:opacity-100"}`}
              data-testid={`gallery-thumb-${i}`}
              aria-label={`Show image ${i + 1}`}
            >
              <img src={src} alt="" className="w-full h-full object-cover" draggable={false} />
            </button>
          ))}
        </div>
      )}

      <div className="mt-8">
        <p className="overline">{v.brand} · {v.year}</p>
        <h1 className="font-display text-5xl md:text-6xl mt-2 tracking-tight">{v.name}</h1>
        <div className="flex items-center gap-6 mt-4 text-sm text-neutral-600">
          <span className="flex items-center gap-1"><Star size={14} fill="currentColor" /> {v.rating}</span>
          <span className="flex items-center gap-1"><MapPin size={14} /> {v.location}</span>
          <span className="uppercase tracking-wider text-xs">{v.category}</span>
        </div>
        <p className="mt-6 text-neutral-700 leading-relaxed max-w-2xl">{v.description}</p>

        <div className="divider-line my-10"></div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <Spec icon={<Users size={18} />} label="Seats" value={v.seats} />
          <Spec icon={<Cog size={18} />} label="Transmission" value={v.transmission} />
          <Spec icon={<Fuel size={18} />} label="Fuel" value={v.fuel} />
          <Spec icon={<Star size={18} />} label="Rating" value={v.rating} />
        </div>

        {v.features?.length > 0 && (
          <>
            <div className="divider-line my-10"></div>
            <p className="overline mb-4">Features</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {v.features.map((f) => (
                <div key={f} className="flex items-center gap-2 text-sm">
                  <Check size={14} className="text-[#004B35]" /> {f}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Spec({ icon, label, value }) {
  return (
    <div>
      <div className="text-neutral-500">{icon}</div>
      <p className="text-xs uppercase tracking-wider text-neutral-500 mt-2">{label}</p>
      <p className="font-display text-xl mt-1 capitalize">{value}</p>
    </div>
  );
}
